import { useI18n } from 'vue-i18n';
import { validateFields } from '@/helpers';
import { isValidNumber } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import { IValidationRules, PhoneDto } from '../../types';
import { PASSWORD_MIN_LENGTH } from '../../constants';

const validateMin = (value: string, min: number, isPatternSet: boolean): boolean => isPatternSet || !min || value.length >= min;
const validateMax = (value: string, max: number, isPatternSet: boolean): boolean => isPatternSet || !max || value.length <= max;
const validateRegExp = (value: string, pattern: string): boolean => new RegExp(pattern).test(value);

export const useValidation = <T extends IValidationRules>(props: T = {} as T) => {
    const { t } = useI18n();

    const rules = {
        required: (value: string) => !!value || !props.required || props.validationMessage || t('validation.fieldRequired'),
        min: (value: string) =>
            !props.min || validateMin(value, props.min, !!props.pattern) || `${t('validation.minCharsRequired')} - ${props.min}`,
        max: (value: string) =>
            !props.max || validateMax(value, props.max, !!props.pattern) || `${t('validation.maxCharsRequired')} - ${props.max}`,
        pattern: (value: string) =>
            !props.pattern || !value || validateRegExp(value, props.pattern) || props.patternError || t('validation.patternRequired')
    };

    const registrationRules = {
        registerPassword: (value: string) => {
            const registerPasswordRules = [
                !value || value.length > PASSWORD_MIN_LENGTH || t('register.passwordMinLength'),
                !props.pattern || !value || validateRegExp(value, props.pattern) || t('register.passwordMustContainCapital')
            ];
            return (
                registerPasswordRules.every(rule => rule === true) || registerPasswordRules.find(rule => typeof rule === 'string') || true
            );
        }
    };

    const createMatchValidator =
        <T>(errorMessage?: string) =>
        (value: T): string | boolean => {
            return isEqual(value, props.referenceValue as T) || (errorMessage ?? t('profile.fieldsMustMatch.'));
        };

    const phoneNumberRules = {
        phoneNumberRequired: (value: PhoneDto): string | boolean =>
            Boolean(value?.countryCode?.length && value?.number?.length) || t('register.fieldIsRequired'),
        phoneNumberValid: (value: PhoneDto): string | boolean =>
            isValidNumber(`${value.countryCode}${value.number}`, 'NO') || t('register.phoneNumberInvalid')
    };

    const validateRules = (value: string | undefined) => validateFields(value, rules);

    const getIconStatus = (value: string | undefined, isValid: boolean) =>
        value ? (isValid ? 'mdi-check-circle' : 'mdi-close-circle') : '';

    return {
        rules,
        registrationRules,
        createMatchValidator,
        phoneNumberRules,
        validateRules,
        getIconStatus
    };
};
